<app-page-shell>
  <app-page-hero eyebrow="People Operations" title="LogiLink HR Command Center" subtitle="Group-aware HR operations across employee master, compliance, payroll, workforce planning, self-service, and analytics.">
    <div actions>
      <button pButton type="button" icon="pi pi-refresh" label="Refresh" severity="secondary" [outlined]="true" (click)="load()"></button>
    </div>
  </app-page-hero>

  @if (showGroupCompanySelector) {
    <app-dashboard-company-scope-selector
      [companyGroupName]="activeGroupName"
      [memberCompanies]="groupCompanies"
      [selectedCompanyId]="selectedGroupCompanyId"
      (selectedCompanyIdChange)="onDashboardCompanyScopeChange($event)">
    </app-dashboard-company-scope-selector>
  }

  @if (loading) {
    <p-progressBar mode="indeterminate"></p-progressBar>
  }

  <section class="scope-grid" aria-label="HR dashboard scope">
    <article class="scope-card scope-card-primary">
      <span class="scope-eyebrow">Current Scope</span>
      <h2>{{ scopeLabel }}</h2>
      <p>{{ scopeHint }}</p>
      <div class="scope-meta">
        <span><i class="fa-solid fa-building"></i>{{ showGroupCompanySelector ? (groupCompanies.length + ' sister companies') : 'Owning company' }}</span>
        <span><i class="fa-solid fa-layer-group"></i>{{ summary.moduleCode || 'HR' }}</span>
        <span><i class="fa-solid fa-shield-halved"></i>{{ summary.subscriptionActive === false ? 'Subscription blocked' : 'Subscription active' }}</span>
      </div>
    </article>
    <article class="scope-card">
      <span class="scope-eyebrow">Rollout State</span>
      <h2>{{ summary.status ? formatCapability(summary.status) : 'HR Dashboard' }}</h2>
      <p>{{ summary.phase ? formatCapability(summary.phase) : 'Phase state pending from API' }}</p>
      <div class="completion-bar">
        <span style="width: 100%"></span>
      </div>
      <div class="scope-meta">
        <span><i class="fa-solid fa-circle-check"></i>Live operations</span>
        <span><i class="fa-solid fa-chart-line"></i>Executive dashboard</span>
      </div>
    </article>
    <article class="scope-card attention-summary" [class.has-risk]="totalAttentionCount > 0">
      <span class="scope-eyebrow">Attention Queue</span>
      <h2>{{ formatValue(totalAttentionCount) }}</h2>
      <p>Open blockers, warnings, verification queues, payroll exceptions, and analytics recommendations.</p>
      <a routerLink="/hr/analytics">Review workforce signals</a>
    </article>
  </section>

  <section class="trend-kpi-grid" aria-label="Executive HR trends">
    @for (kpi of trendKpis; track kpi.label) {
      <a class="trend-kpi-card" [class.is-risk]="kpi.trendDirection === 'alert'" [class.is-good]="kpi.tone === 'success'" [routerLink]="kpi.route">
        <div class="trend-kpi-top">
          <span><i [class]="kpi.icon"></i>{{ kpi.label }}</span>
          <p-tag [value]="kpi.trendLabel" [severity]="severityForTone(kpi.tone)"></p-tag>
        </div>
        <strong>{{ kpi.value }}</strong>
        <p>{{ kpi.detail }}</p>
        <div class="sparkline" aria-hidden="true">
          @for (point of kpi.series; track $index) {
            <span [style.height.%]="sparkHeight(point, kpi.series)"></span>
          }
        </div>
      </a>
    }
  </section>

  <section class="dashboard-section chart-grid" aria-label="HR trend and mix charts">
    <app-page-data-panel title="Workforce Trend" subtitle="Headcount, active workforce, and open attention queue trajectory for the selected company scope.">
      <div class="chart-frame chart-frame-wide">
        <p-chart type="line" [data]="workforceTrendChartData" [options]="lineChartOptions" height="300px"></p-chart>
      </div>
    </app-page-data-panel>

    <app-page-data-panel title="Headcount Mix" subtitle="Active, onboarding, and remaining employee population.">
      <div class="chart-frame chart-frame-compact">
        <p-chart type="doughnut" [data]="headcountMixChartData" [options]="doughnutChartOptions" height="300px"></p-chart>
      </div>
    </app-page-data-panel>

    <app-page-data-panel title="Compliance Exposure" subtitle="Document validity, missing requirements, expiry pressure, and review backlog.">
      <div class="chart-frame chart-frame-compact">
        <p-chart type="doughnut" [data]="complianceChartData" [options]="doughnutChartOptions" height="300px"></p-chart>
      </div>
    </app-page-data-panel>

    <app-page-data-panel title="Time and Payroll Workload" subtitle="Attendance exceptions, timesheet closure, payroll exceptions, and run readiness.">
      <div class="chart-frame">
        <p-chart type="bar" [data]="timePayrollChartData" [options]="barChartOptions" height="300px"></p-chart>
      </div>
    </app-page-data-panel>

    <app-page-data-panel title="Talent Pipeline and Learning" subtitle="Recruitment funnel, offer pipeline, and learning compliance workload.">
      <div class="chart-frame">
        <p-chart type="bar" [data]="talentPipelineChartData" [options]="barChartOptions" height="300px"></p-chart>
      </div>
    </app-page-data-panel>

    <app-page-data-panel title="Readiness and Rostering" subtitle="Operational readiness, driver qualification coverage, weekly roster work, and conflicts.">
      <div class="chart-frame">
        <p-chart type="bar" [data]="readinessRosterChartData" [options]="barChartOptions" height="300px"></p-chart>
      </div>
    </app-page-data-panel>

    <app-page-data-panel title="Employee Relations and Analytics" subtitle="Case risk, acknowledgements, performance goals, and optimization recommendations.">
      <div class="chart-frame chart-frame-compact">
        <p-chart type="doughnut" [data]="caseAnalyticsChartData" [options]="doughnutChartOptions" height="300px"></p-chart>
      </div>
    </app-page-data-panel>
  </section>

  <app-page-data-panel title="Operating Lane Health" subtitle="Readiness ratios derived from live HR operations, shown as lane-level closure and risk pressure." [resultText]="scopeLabel">
    <div class="lane-grid">
      @for (lane of operatingLanes; track lane.label) {
        <a class="lane-card" [routerLink]="lane.route" [class.is-risk]="lane.tone === 'danger'" [class.is-warn]="lane.tone === 'warn'">
          <div class="lane-heading">
            <strong>{{ lane.label }}</strong>
            <span>{{ percentText(lane.current, lane.total) }}</span>
          </div>
          <div class="lane-progress" aria-hidden="true">
            <span [style.width.%]="lane.ratio * 100"></span>
          </div>
          <p>{{ lane.detail }}</p>
          <small>{{ formatValue(lane.current) }} clear / {{ formatValue(lane.total) }} total</small>
        </a>
      }
    </div>
  </app-page-data-panel>

  <section class="dashboard-section" aria-label="HR attention queue">
    <div class="section-heading">
      <div>
        <span>Immediate Attention</span>
        <h3>Exceptions, blockers, and approval queues</h3>
      </div>
      <p-tag [value]="formatValue(totalAttentionCount) + ' total'" [severity]="totalAttentionCount > 0 ? 'warn' : 'success'"></p-tag>
    </div>
    <div class="attention-grid">
      @for (item of attentionItems; track item.label) {
        <a class="attention-card" [class.is-clear]="item.value === 0" [routerLink]="item.route">
          <div>
            <strong>{{ item.label }}</strong>
            <p>{{ item.hint }}</p>
          </div>
          <p-tag [value]="formatValue(item.value)" [severity]="severityForTone(item.value > 0 ? item.tone : 'success')"></p-tag>
        </a>
      }
    </div>
  </section>

  <section class="dashboard-section" aria-label="HR domain coverage">
    <div class="section-heading">
      <div>
        <span>Operational Domains</span>
        <h3>Full HR module control surface</h3>
      </div>
      <p-tag [value]="domainCards.length + ' domains'" severity="info"></p-tag>
    </div>
    <div class="domain-grid">
      @for (domain of domainCards; track domain.title) {
        <a class="domain-card" [class.has-attention]="domain.attention > 0" [routerLink]="domain.route">
          <div class="domain-top">
            <i [class]="domain.icon"></i>
            <p-tag [value]="domain.status" [severity]="severityForTone(domain.attention > 0 ? 'warn' : 'success')"></p-tag>
          </div>
          <h4>{{ domain.title }}</h4>
          <p>{{ domain.description }}</p>
          <div class="domain-metrics">
            @for (metric of domain.metrics; track metric.label) {
              <div>
                <span>{{ metric.label }}</span>
                <strong>{{ formatValue(metric.value) }}</strong>
                @if (metric.hint) {
                  <small>{{ metric.hint }}</small>
                }
              </div>
            }
          </div>
        </a>
      }
    </div>
  </section>
</app-page-shell>

